import { FC, useMemo } from 'react'
import { useGetMonitoringPagesQuery } from 'src/reactQuery/useGetMonitoringPagesQuery'
import { Skeleton } from 'src/components/Skeleton'
import { CountriesCell } from './CountriesCell'
import classes from './competitorsComparison.module.scss'
import { PlatformsCell } from './PlatformsCell'
import { AdLibraryEmptyState } from '../EmptyState'
import { ReactComponent as ComparisonEmptyIcon } from '../comperesion-empty.svg'

const TABLE_HEADERS = [
  'Competitors',
  'Active Ads',
  'Languages',
  'Platforms',
  'Countries',
]

// From competitorsComparison.module.scss: .competitorLogo 24x24 radius 4 ·
// .competitorName font-size 14 · .activeAdsPill height 28 radius 24 ·
// .numericCell font-size 16 · .platformIconWrapper 24x24 radius 20.
// .section has min-height 389px and the header is 40px, so four rows keep the
// loading table inside the footprint the loaded table occupies.
const LOGO_SIZE = 24
const LOGO_RADIUS = 4
const NAME_HEIGHT = 14
const PILL_HEIGHT = 28
const PILL_RADIUS = 24
const PILL_WIDTH = 96
const NUMERIC_HEIGHT = 16
const PLATFORM_ICON_SIZE = 24
const COUNTRY_CHIP_HEIGHT = 26
const COUNTRY_CHIP_RADIUS = 20
const COMPARISON_SKELETON_ROWS = 4

interface ComparisonRow {
  activeAds: number
  countries: { code?: string; name: string }[]
  id: string
  languages: number
  logo: string
  name: string
  platforms: { count: number; name: string }[]
}

export const CompetitorsComparison: FC = () => {
  const { data, isLoading } = useGetMonitoringPagesQuery()
  const pages = data?.data || []

  const rows: ComparisonRow[] = useMemo(
    () =>
      pages
        .map((page) => {
          const snap = page.latest_snapshot
          if (!snap) return null
          return {
            activeAds: snap.active_count,
            countries: snap.target_countries.map((c) => ({
              code: c.code,
              name: c.name,
            })),
            id: page._id,
            languages: Object.keys(snap.languages || {}).length,
            logo:
              page.page_picture ||
              `https://graph.facebook.com/${page.page_id}/picture?type=square`,
            name: page.page_name || 'Unknown',
            platforms: Object.entries(snap.platforms || {})
              .sort(([, a], [, b]) => b - a)
              .map(([name, count]) => ({ count, name })),
          } as ComparisonRow
        })
        .filter((row): row is ComparisonRow => row !== null),
    [pages]
  )

  // rows is [] before the response lands, so the loading branch must precede
  // the empty state — otherwise the table claims "No comparison yet" while the
  // query is still in flight.
  const isInitialLoading = isLoading && !data
  const hasData = isInitialLoading || rows.length > 0

  return (
    <div
      className={`${classes.section} ${hasData ? classes.sectionWithData : ''}`}
    >
      <h2 className={classes.title}>Competitors Comparison</h2>
      {hasData ? (
        <>
          <div className={classes.tableHeader}>
            {TABLE_HEADERS.map((header) => (
              <span className={classes.tableHeaderCell} key={header}>
                {header}
              </span>
            ))}
          </div>
          <div className={classes.tableBody}>
            {isInitialLoading
              ? Array.from({ length: COMPARISON_SKELETON_ROWS }).map(
                  (_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={classes.tableRow} key={index}>
                      <div className={classes.cellCompetitor}>
                        <Skeleton
                          height={LOGO_SIZE}
                          radius={LOGO_RADIUS}
                          width={LOGO_SIZE}
                        />
                        <Skeleton height={NAME_HEIGHT} width="60%" />
                      </div>

                      <div className={classes.cellActiveAds}>
                        <Skeleton
                          height={PILL_HEIGHT}
                          radius={PILL_RADIUS}
                          width={PILL_WIDTH}
                        />
                      </div>

                      <div className={classes.cellCenter}>
                        <Skeleton height={NUMERIC_HEIGHT} width={24} />
                      </div>

                      <div className={classes.cellCenter}>
                        <div className={classes.platformIcons}>
                          <Skeleton circle width={PLATFORM_ICON_SIZE} />
                          <Skeleton circle width={PLATFORM_ICON_SIZE} />
                        </div>
                      </div>

                      <div className={classes.cellRight}>
                        <Skeleton
                          height={COUNTRY_CHIP_HEIGHT}
                          radius={COUNTRY_CHIP_RADIUS}
                          width={88}
                        />
                      </div>
                    </div>
                  )
                )
              : rows.map((row) => {
                  return (
                    <div className={classes.tableRow} key={row.id}>
                      <div className={classes.cellCompetitor}>
                        <img
                          alt={row.name}
                          className={classes.competitorLogo}
                          src={row.logo}
                        />
                        <span className={classes.competitorName}>
                          {row.name}
                        </span>
                      </div>

                      <div className={classes.cellActiveAds}>
                        <span className={classes.activeAdsPill}>
                          <span className={classes.activeAdsNumber}>
                            {row.activeAds}
                          </span>
                          <span className={classes.activeAdsLabel}>
                            Active Ads
                          </span>
                        </span>
                      </div>

                      <div className={classes.cellCenter}>
                        <span className={classes.numericCell}>
                          {row.languages}
                        </span>
                      </div>

                      <div className={classes.cellCenter}>
                        <PlatformsCell platforms={row.platforms} />
                      </div>

                      <div className={classes.cellRight}>
                        <CountriesCell countries={row.countries} />
                      </div>
                    </div>
                  )
                })}
          </div>
        </>
      ) : (
        <AdLibraryEmptyState
          description={
            <>
              To compare competitors, you need to find and add them from
              <br />
              the Ad Library section. Once added, you&apos;ll be able to analyze
              and
              <br />
              compare their performance here.
            </>
          }
          icon={ComparisonEmptyIcon}
          title="No comparison yet"
        />
      )}
    </div>
  )
}
